import axios, { type AxiosInstance, type AxiosRequestConfig } from "axios";
import { defineMessages } from "react-intl";
import { getSessionToken } from "@/modules/session/functions";
import { env } from "@/services/env.service";

const API_URL = env.REACT_APP_API;
export const HTTP_UNAUTHORIZED = 401;
export const HTTP_INTERNAL_SERVER_ERROR = 500;

type UnauthorizedHandler = () => void;

export const apiClientHooks: {
  onUnauthorized: UnauthorizedHandler[];
} = {
  onUnauthorized: [],
};

const translate = defineMessages({
  networkError: {
    id: "app.error.network_error",
    defaultMessage: "Es sieht so aus, als wären Sie offline...",
  },
  timeoutError: {
    id: "app.error.timeout",
    defaultMessage:
      "Die Anfrage hat zu lange gedauert. Bitte erneut versuchen.",
  },
});

// handle 401 requests
const addUnauthorizedInterceptor = (client: AxiosInstance) =>
  client.interceptors.response.use(null, async (err: any) => {
    // worst case / show message and cancel request
    if (err.message === "Network Error") {
      const { default: store } = await import("../store/store");
      const { showNotificationMessage } = await import("../modules/uiState");

      store.dispatch(
        showNotificationMessage(
          // missing formatMessage
          translate.networkError.defaultMessage,
        ),
      );
      return Promise.reject(err);
    }

    // timeout handling
    if (
      err.code === "ECONNABORTED" ||
      err.message?.toLowerCase().includes("timeout")
    ) {
      const { default: store } = await import("../store/store");
      const { showNotificationMessage } = await import("../modules/uiState");

      store.dispatch(
        showNotificationMessage(translate.timeoutError.defaultMessage),
      );
      return Promise.reject(err);
    }

    if (err.response && err.response.status === HTTP_UNAUTHORIZED) {
      apiClientHooks.onUnauthorized.forEach((fn) => fn());
    }

    return Promise.reject(err);
  });

// Pure params serializer: JSON-stringify `filter` when it's an object,
// and encode other params using encodeURIComponent. This makes it safe
// for callers to pass `params: { filter: { estateId: 890 } }` and have
// the query built as filter=%7B%22estateId%22%3A890%7D (URL-encoded JSON).
const paramsSerializer = (params: Record<string, any> = {}): string => {
  const parts: string[] = [];
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value === null || typeof value === "undefined") return;

    if (key === "filter") {
      // If filter is an object, stringify it first. If it's already a
      // string, leave it as is.
      const filterVal =
        typeof value === "string" ? value : JSON.stringify(value);
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(filterVal)}`);
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((v) => {
        parts.push(`${encodeURIComponent(key)}[]=${encodeURIComponent(v)}`);
      });
      return;
    }

    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
  });
  return parts.join("&");
};

export const apiClient = (
  options?: AxiosRequestConfig,
  bearerToken?: string,
): AxiosInstance => {
  const token = bearerToken || getSessionToken();

  const client = axios.create({
    headers: {
      "Content-Type": "application/json; charset=utf-8",
      Authorization: `Bearer ${token}`,
    },
    // `baseURL` will be prepended to `url` unless `url` is absolute.
    baseURL: API_URL,
    // default request timeout (can be overridden per call via options)
    timeout: 60000,
    // custom params serializer to handle `filter` objects
    paramsSerializer,
    // upload, timeout, download, ..usw
    ...options,
  });

  addUnauthorizedInterceptor(client);

  return client;
};
